import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    buildNotificationDedupeKey,
    buildResolveAllPayload,
    fetchNotifications,
    mapThresholdDirectionToBreachSide,
    markAllNotificationsRead,
    markNotificationRead,
    markNotificationUnread,
    resolveAllActiveNotifications,
    resolveNotificationByDedupeKey,
    upsertActiveNotification,
} from '@/services/notificationServices';

const NotificationContext = createContext(null);
const POLL_INTERVAL_MS = 30000;

const toPositionKey = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');

const normalizeNotification = (item) => {
    if (!item) return null;
    const bridge = item.bridge || item.bridgeName || 'unknown';
    const position = item.position || item.sensorPosition || item.positionKey || '';
    const positionKey = toPositionKey(item.positionKey || position);
    const breachSide = item.breach_side || mapThresholdDirectionToBreachSide(item.direction);
    const dedupeKey = item.dedupe_key || item.dedupeKey || buildNotificationDedupeKey({
        bridge,
        position,
        breachSide,
    });

    return {
        ...item,
        bridge,
        position,
        positionKey,
        breach_side: breachSide,
        dedupe_key: dedupeKey,
        status: item.status || 'active',
        read: Boolean(item.read),
    };
};

const getTimestamp = (item) => new Date(item?.updated_at || item?.created_at || 0).getTime();

const normalizeList = (list = []) => {
    return list
        .map(normalizeNotification)
        .filter(Boolean)
        .sort((a, b) => getTimestamp(b) - getTimestamp(a));
};

const nowIso = () => new Date().toISOString();
const mergeNotificationIntoList = (list, notification) => normalizeList([
    notification,
    ...list.filter((item) => item.dedupe_key !== notification?.dedupe_key),
]);

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const refreshNotifications = useCallback(async ({ silent = false } = {}) => {
        if (!silent) {
            setIsLoading(true);
        }

        try {
            setLoadError(null);
            const data = await fetchNotifications();
            setNotifications(normalizeList(data || []));
        } catch (error) {
            console.error('Failed to refresh notifications', error);
            setLoadError('Failed to load notifications');
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        refreshNotifications();

        const intervalId = window.setInterval(() => {
            refreshNotifications({ silent: true });
        }, POLL_INTERVAL_MS);

        const handleFocus = () => {
            refreshNotifications({ silent: true });
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshNotifications({ silent: true });
            }
        };

        window.addEventListener('focus', handleFocus);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', handleFocus);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshNotifications]);

    const recordNotification = useCallback((payload) => {
        return upsertActiveNotification(payload)
            .then((row) => {
                const normalized = normalizeNotification(row);
                if (!normalized) return null;
                setNotifications((prev) => mergeNotificationIntoList(prev, normalized));
                setLoadError(null);
                return normalized.id;
            })
            .catch((error) => {
                console.error('Failed to upsert live notification', error);
                return null;
            });
    }, []);

    const resolveBreach = useCallback(({ bridgeName, positionKey, direction }) => {
        return resolveNotificationByDedupeKey({
            bridge: bridgeName,
            position: positionKey,
            direction,
        })
            .then((row) => {
                if (!row) return null;
                const normalized = normalizeNotification(row);
                if (!normalized) return null;
                setNotifications((prev) => mergeNotificationIntoList(prev, normalized));
                return normalized.id;
            })
            .catch((error) => {
                console.error('Failed to resolve live notification', error);
                return null;
            });
    }, []);

    const markAsRead = useCallback((id) => {
        return markNotificationRead(id)
            .then(() => {
                setNotifications((prev) =>
                    prev.map((item) => (item.id === id ? { ...item, read: true } : item))
                );
            })
            .catch((error) => {
                console.error('Failed to mark notification as read', error);
            });
    }, []);

    const markAsUnread = useCallback((id) => {
        return markNotificationUnread(id)
            .then(() => {
                setNotifications((prev) =>
                    prev.map((item) => (item.id === id ? { ...item, read: false } : item))
                );
            })
            .catch((error) => {
                console.error('Failed to mark notification as unread', error);
            });
    }, []);

    const markAllAsRead = useCallback(() => {
        return markAllNotificationsRead()
            .then(() => {
                setNotifications((prev) =>
                    prev.map((item) => (
                        (item.status || 'active') === 'active'
                            ? { ...item, read: true }
                            : item
                    ))
                );
            })
            .catch((error) => {
                console.error('Failed to mark all notifications as read', error);
            });
    }, []);

    const resolveAllActive = useCallback(() => {
        const resolvedAt = nowIso();
        return resolveAllActiveNotifications(resolvedAt)
            .then(() => {
                const payload = buildResolveAllPayload(resolvedAt);
                setNotifications((prev) =>
                    prev.map((item) => (
                        (item.status || 'active') === 'active'
                            ? {
                                ...item,
                                ...payload,
                                updated_at: payload.resolved_at,
                            }
                            : item
                    ))
                );
            })
            .catch((error) => {
                console.error('Failed to resolve all active notifications', error);
            });
    }, []);

    const value = useMemo(
        () => ({
            notifications,
            isLoading,
            loadError,
            refreshNotifications,
            recordNotification,
            resolveBreach,
            markAsRead,
            markAsUnread,
            markAllAsRead,
            resolveAllActive,
            clearHistory: resolveAllActive,
            unreadCount: notifications.filter(
                (notification) => (notification.status || 'active') === 'active' && !notification.read
            ).length,
        }),
        [isLoading, loadError, markAllAsRead, markAsRead, markAsUnread, notifications, recordNotification, refreshNotifications, resolveAllActive, resolveBreach]
    );

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotifications = () => {
    const ctx = useContext(NotificationContext);
    if (!ctx) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return ctx;
};

export default NotificationProvider;
